import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Select,
  TimePicker,
  Input,
  Button,
  Space,
  Typography,
  Divider,
  message,
} from "antd";

import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  CalendarOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { saveDoctorAvailability } from './../../Services/adminServices';
import { useMasterData } from "../../Components/Common/commonEvents";
import DoctorLayout from "../../Layouts/Doctor/DoctorLayout";

const { Title, Text } = Typography;
const { Option } = Select;

const getServiceTimeValue = (time) => {
  if (!time) return null;

  if (typeof time !== "string") return time;

  const [hours, minutes] = time.substring(11, 16).split(":");

  return dayjs()
    .hour(Number(hours))
    .minute(Number(minutes))
    .second(0)
    .millisecond(0);
};

export default function DoctorAvailabilityScreen() {
  const sessionUserData = JSON.parse(
    sessionStorage.getItem("userData") || "{}"
  );

  const [loading, setLoading] = useState(false);
  const [doctorId, setDoctorId] = useState(null);

  const [availabilityList, setAvailabilityList] = useState([
    {
      key: Date.now(),
      DayId: undefined,
      StartTime: null,
      EndTime: null,
      SlotDuration: 15,
      Notes: "",
    },
  ]);

  const { usersByRole, doctorSlots, fetchUsersByRole, fetchDoctorSlots } = useMasterData();

  useEffect(() => {
    if (!sessionUserData?.OrgId) return;

    fetchUsersByRole(sessionUserData.OrgId, 2);
  }, [sessionUserData?.OrgId]);

  useEffect(() => {
    if (!sessionUserData?.OrgId || !doctorId) return;

    fetchDoctorSlots(sessionUserData.OrgId, doctorId);
  }, [sessionUserData?.OrgId, doctorId]);

  const days = [
    { Id: 0, Name: "Select All Days" },
    { Id: 1, Name: "Sunday" },
    { Id: 2, Name: "Monday" },
    { Id: 3, Name: "Tuesday" },
    { Id: 4, Name: "Wednesday" },
    { Id: 5, Name: "Thursday" },
    { Id: 6, Name: "Friday" },
    { Id: 7, Name: "Saturday" },
  ];

  const slotDurations = [5, 10, 15, 20, 30, 45, 60];

  useEffect(() => {
    if (!doctorSlots?.length) return;

    const formattedData = doctorSlots.map((slot) => ({
      key: slot.Id,

      Id: slot.Id,

      DayId: slot.DayId,

      StartTime: slot.StartTime
        ? getServiceTimeValue(slot.StartTime)
        : null,

      EndTime: slot.EndTime
        ? getServiceTimeValue(slot.EndTime)
        : null,

      SlotDuration: slot.SlotDuration || 15,

      Notes: slot.Notes || "",
    }));

    setAvailabilityList(formattedData);

  }, [doctorSlots]);

  const addRow = () => {
    setAvailabilityList((prev) => [
      ...prev,
      {
        key: Date.now(),
        DayId: undefined,
        StartTime: null,
        EndTime: null,
        SlotDuration: 15,
        Notes: "",
        IsActive: 1,
      },
    ]);
  };

  const removeRow = (key) => {
    setAvailabilityList((prev) =>
      prev.flatMap((item) => {
        if (item.key !== key) return item;
  
        // Existing record -> mark inactive
        if (item.Id > 0) {
          return {
            ...item,
            IsActive: 0,
          };
        }
  
        // New unsaved row -> remove completely
        return [];
      })
    );
  };

  const handleChange = (key, field, value) => {

    // DAY VALIDATION
    if (field === "DayId") {

      // SELECT ALL
      if (value === 0) {

        const allDaysData = days
          .filter((d) => d.Id !== 0)
          .map((day, index) => ({
            key: Date.now() + index,
            DayId: day.Id,
            StartTime: null,
            EndTime: null,
            SlotDuration: 15,
            Notes: "",
            IsActive: 1,
          }));

        setAvailabilityList(allDaysData);

        return;
      }
    }

    setAvailabilityList((prev) =>
      prev.map((item) =>
        item.key === key
          ? {
            ...item,
            [field]: value,
          }
          : item
      )
    );
  };

  const handleSaveAvailability = async () => {
    if (!doctorId) {
      message.error("Please select doctor");
      return;
    }

    const hasInvalid = availabilityList.some(
      (item) =>
        !item.DayId ||
        !item.StartTime ||
        !item.EndTime
    );

    if (hasInvalid) {
      message.error("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        OrgId: sessionUserData?.OrgId,
        DoctorId: doctorId,
        UserId: sessionUserData?.Id,

        Json: availabilityList.map((item) => ({
          Id: item.Id || 0,
          DayId: item.DayId,
          StartTime: dayjs(item.StartTime).format("HH:mm"),
          EndTime: dayjs(item.EndTime).format("HH:mm"),
          SlotDuration: item.SlotDuration,
          Notes: item.Notes,
          IsActive: item.IsActive ?? 1,
        })),
      };

      const response = await saveDoctorAvailability(payload);

      if (
        response?.success &&
        response?.code === 1
      ) {
        message.success(
          "Doctor availability saved successfully"
        );
        fetchDoctorSlots(sessionUserData.OrgId, doctorId);

      } else {
        message.error(
          response?.message ||
          "Failed to save availability"
        );
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
        "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <DoctorLayout>

      <div className="container-fluid">

        {/* HEADER */}
        <Card
          bordered={false}
          className="shadow-sm mb-4"
          style={{
            borderRadius: "24px",
            background:
              "linear-gradient(135deg,#052e16,#166534,#22c55e)",
          }}
        >
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">

            <div>
              <Title
                level={2}
                className="mb-1 text-white"
              >
                Doctor Availability
              </Title>

              <Text
                className="text-white"
                style={{ opacity: 0.9 }}
              >
                Manage doctor slots and schedules
              </Text>
            </div>

            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: 72,
                height: 72,
                borderRadius: 20,
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
              }}
            >
              <CalendarOutlined
                style={{
                  fontSize: 34,
                  color: "#fff",
                }}
              />
            </div>
          </div>
        </Card>

        {/* FORM */}
        <Card
          bordered={false}
          className="shadow-sm"
          style={{
            borderRadius: "24px",
          }}
        >
          <Row gutter={[20, 20]}>

            {/* DOCTOR */}
            <Col xs={24} md={10}>
              <Text strong>
                <UserOutlined className="me-2 text-success" />
                Select Doctor
              </Text>

              <Select
                placeholder="Choose Doctor"
                size="large"
                className="w-100 mt-2"
                value={doctorId}
                onChange={setDoctorId}
              >
                {(usersByRole || []).map((doc) => (
                  <Option
                    key={doc.Id}
                    value={doc.Id}
                  >
                    {doc.Name}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* ADD ROW */}
            <Col
              xs={24}
              md={14}
              className="d-flex align-items-end justify-content-end"
            >
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={addRow}
                style={{
                  background:
                    "linear-gradient(135deg,#16a34a,#22c55e)",
                  border: "none",
                  borderRadius: "12px",
                  height: "48px",
                  paddingInline: "24px",
                }}
              >
                Add Availability
              </Button>
            </Col>
          </Row>

          <Divider />

          {/* ROWS */}
          <Space
            direction="vertical"
            size={20}
            className="w-100"
          >
           {availabilityList
  .filter((item) => item.IsActive !== 0)
  .map((item) => (
              <Card
                key={item.key}
                bordered={false}
                className="shadow-sm"
                style={{
                  borderRadius: "20px",
                  background: "#f8fafc",
                }}
              >
                <Row gutter={[16, 16]}>

                  {/* DAY */}
                  <Col xs={24} md={4}>
                    <Text strong>Day</Text>

                    <Select
                      placeholder="Select Day"
                      size="large"
                      className="w-100 mt-2"
                      value={item.DayId}
                      onChange={(value) =>
                        handleChange(
                          item.key,
                          "DayId",
                          value
                        )
                      }
                    >
                      {days.map((day) => (
                        <Option
                          key={day.Id}
                          value={day.Id}
                        >
                          {day.Name}
                        </Option>
                      ))}
                    </Select>
                  </Col>

                  {/* START */}
                  <Col xs={24} md={4}>
                    <Text strong>Start Time</Text>

                    <TimePicker
                      size="large"
                      className="w-100 mt-2"
                      format="HH:mm"
                      minuteStep={15}
                      value={getServiceTimeValue(item.StartTime)}
                      onChange={(value) =>
                        handleChange(
                          item.key,
                          "StartTime",
                          value
                        )
                      }
                    />
                  </Col>

                  {/* END */}
                  <Col xs={24} md={4}>
                    <Text strong>End Time</Text>

                    <TimePicker
                      size="large"
                      className="w-100 mt-2"
                      format="HH:mm"
                      minuteStep={15}
                      value={getServiceTimeValue(item.EndTime)}
                      onChange={(value) =>
                        handleChange(
                          item.key,
                          "EndTime",
                          value
                        )
                      }
                    />
                  </Col>

                  {/* SLOT */}
                  <Col xs={24} md={4}>
                    <Text strong>Slot Duration</Text>

                    <Select
                      size="large"
                      className="w-100 mt-2"
                      value={item.SlotDuration}
                      onChange={(value) =>
                        handleChange(
                          item.key,
                          "SlotDuration",
                          value
                        )
                      }
                    >
                      {slotDurations.map((slot) => (
                        <Option
                          key={slot}
                          value={slot}
                        >
                          {slot} mins
                        </Option>
                      ))}
                    </Select>
                  </Col>

                  {/* NOTES */}
                  <Col xs={24} md={6}>
                    <Text strong>Notes</Text>

                    <Input
                      size="large"
                      className="mt-2"
                      placeholder="Morning Shift"
                      value={item.Notes}
                      onChange={(e) =>
                        handleChange(
                          item.key,
                          "Notes",
                          e.target.value
                        )
                      }
                    />
                  </Col>

                  {/* DELETE */}
                  <Col
                    xs={24}
                    md={2}
                    className="d-flex align-items-end"
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      size="large"
                      onClick={() =>
                        removeRow(item.key)
                      }
                      disabled={
                        availabilityList.length === 1
                      }
                      style={{
                        borderRadius: "12px",
                        width: "100%",
                      }}
                    />
                  </Col>
                </Row>
              </Card>
            ))}
          </Space>

          {/* SAVE */}
          <div className="text-end mt-4 sticky-bottom">
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              loading={loading}
              onClick={handleSaveAvailability}
              style={{
                height: "52px",
                borderRadius: "14px",
                paddingInline: "32px",
                border: "none",
                background:
                  "linear-gradient(135deg,#15803d,#22c55e)",
                boxShadow:
                  "0 12px 24px rgba(34,197,94,0.35)",
              }}
            >
              Save Availability
            </Button>
          </div>
        </Card>
      </div>
    </DoctorLayout>
  );
}
